import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OrganizationSwitcher from './OrganizationSwitcher';
import './DashboardLayout.css';
import '../styles/DashboardTheme.css';
import { useEffect } from 'react';

const navigationItems = [
	{ name: 'Dashboard Home', icon: 'dashboard', path: '/dashboard' },
	{ name: 'Announcements', icon: 'campaign' },
	{ name: 'Notifications', icon: 'notifications' },
	{ name: 'Events', icon: 'event' },
	{ name: 'Files', icon: 'folder' },
	{ name: 'People', icon: 'people' },
	{ name: 'Teams', icon: 'groups' },
	{ name: 'Ministries', icon: 'church' },
	// { name: 'Reports', icon: 'assessment' },
	{ name: 'Organization', icon: 'business' },
	{ name: 'Subscription', icon: 'payments' },
];

function DashboardLayout({ children }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { logout, orgData, userData, adminOrgs, hasActiveSubscription } =
		useAuth();
	const primaryColor = orgData?.primaryColor || '#4b95a3';

	// Filter navigation items based on subscription status
	const filteredNavigationItems = hasActiveSubscription
		? navigationItems
		: navigationItems.filter(
				(item) =>
					item.name === 'Organization' || item.name === 'Subscription'
		  );

	const completeNavigationItems =
		userData?.accessLevel === 'SuperAdmin'
			? [
					...filteredNavigationItems,
					{ name: 'Admin', icon: 'admin_panel_settings' },
			  ]
			: filteredNavigationItems;

	const sidebarStyle = {
		'--primary-color': primaryColor,
		'--primary-color-light': `${primaryColor}1A`,
	};

	const handleLogout = () => {
		logout();
		navigate('/dashboard/login');
	};

	const isActive = (item) => {
		const itemPath = item.path || `/dashboard/${item.name.toLowerCase()}`;
		return location.pathname === itemPath;
	};

	// Redirect if trying to access restricted page
	useEffect(() => {
		const currentPath = location.pathname;
		const isRestrictedPath =
			!hasActiveSubscription &&
			!currentPath.includes('/dashboard/organization') &&
			!currentPath.includes('/dashboard/subscription') &&
			currentPath !== '/dashboard';

		if (isRestrictedPath) {
			navigate('/dashboard/subscription');
		}
	}, [location.pathname, hasActiveSubscription, navigate]);

	// Set CSS variables for org colors if they exist
	useEffect(() => {
		if (orgData) {
			document.documentElement.style.setProperty(
				'--org-primary-color',
				orgData.primaryColor
			);
			document.documentElement.style.setProperty(
				'--org-secondary-color',
				orgData.secondaryColor
			);
			document.documentElement.style.setProperty(
				'--org-accent-color',
				orgData.accentColor
			);
		}
	}, [orgData]);

	return (
		<div className='dashboard-container'>
			<nav
				className='dashboard-sidebar'
				style={sidebarStyle}>
				{adminOrgs && adminOrgs.length > 1 && (
					<div className='org-switcher-container'>
						<OrganizationSwitcher />
					</div>
				)}
				<div className='sidebar-nav-items-container'>
					{completeNavigationItems.map((item) => (
						<Link
							key={item.name}
							to={
								item.path ||
								`/dashboard/${item.name.toLowerCase()}`
							}
							className={`sidebar-nav-item ${
								isActive(item) ? 'active' : ''
							}`}
							style={
								isActive(item) ? { color: primaryColor } : {}
							}>
							<span className='material-icons'>{item.icon}</span>
							<span>{item.name}</span>
						</Link>
					))}
				</div>
				<div className='logout-container'>
					<button
						onClick={handleLogout}
						className='sidebar-nav-item-logout'>
						<span className='material-icons'>logout</span>
						<span>Logout</span>
					</button>
				</div>
			</nav>
			<main className='dashboard-content'>{children}</main>
		</div>
	);
}

export default DashboardLayout;
